export default class AnnotationsByCoursewareCache {
  constructor(motivation) {
    this.motivation = motivation;
    this.initialized = false;
    this.initializing = false;
    this._annotations = {};
    this._annotationsByCoursewareId = {};
    this._subscriptions = {};
    this.coursewareIDs=[];
  }

  getAnnotationsByCoursewareId(coursewareId) {
    if (!this._annotationsByCoursewareId[coursewareId]) {
      return [];
    }
    return this._annotationsByCoursewareId[coursewareId].map((annotationId) => this._annotations[annotationId]).filter((annotation) => annotation !== undefined);
  }

  updateAnnotation(annotation) {
    this._annotations[annotation.id] = {
      ...this._annotations[annotation.id],
      annotation,
    };
  }

  getAnnotation(id) {
    return this._annotations[id];
  }

  _createUnsubscribe(coursewareId, listener) {
    return () => {
      const listenerIndex = this._subscriptions[coursewareId].indexOf(listener);
      this._subscriptions[coursewareId].splice(listenerIndex, 1);
    };
  }

  _notifySubscribers(coursewareIds) {
    for (const coursewareId of coursewareIds) {
      if (this._subscriptions[coursewareId]) {
        const listeners = this._subscriptions[coursewareId];
        const annotations = this.getAnnotationsByCoursewareId(coursewareId);
        listeners.forEach((listener) => listener(annotations));
      }
    }
  }

  _notifySubscribersByAnnotationId(annotationId) {
    const annotation = this._annotations[annotationId];
    const coursewareIds = [
      ...annotation.target[0].ancestry?.map(({ elementId }) => elementId),
      annotation.target[0].id,
    ];
    this._notifySubscribers(coursewareIds);
  }

  subscribe(coursewareId, listener) {
    this._subscriptions[coursewareId] = this._subscriptions[coursewareId] || [];
    this._subscriptions[coursewareId].push(listener);
    const annotations = this.getAnnotationsByCoursewareId(coursewareId);
    listener(annotations);

    return this._createUnsubscribe(coursewareId, listener);
  }

  saveAnnotation(annotation) {
    const existingAnnotation = this.getAnnotation(annotation.id);
    if (existingAnnotation) {
      this._annotations[annotation.id] = annotation;
    } else {
      this._addAnnotation(annotation);
    }

    this.initialized = true;
    this._notifySubscribersByAnnotationId(annotation.id);
  }

  saveAnnotations(annotations) {
    annotations.forEach((item) => {
      const existingAnnotation = this.getAnnotation(item.id);
      if (existingAnnotation) {
        this._annotations[item.id] = item;
        // Re-index under updated ancestry IDs (e.g. lessonId added by richAncestry on lesson-scoped fetch)
        const target = item.target?.[0];
        if (target) {
          this._addAnnotationIdByTarget(target.id, item.id);
          target.ancestry?.forEach(({ elementId }) => {
            this._addAnnotationIdByTarget(elementId, item.id);
          });
        }
      } else {
        this._addAnnotation(item);
      }
    });
    this.initialized = true;
    this._notifySubscribers(Object.keys(this._annotationsByCoursewareId));
  }

  deleteAnnotation(annotationId) {
    if (this._annotations[annotationId]) {
      // Save courseware item IDs to be notified
      const notifyingCoursewareIds = Object.keys(this._annotationsByCoursewareId);

      delete this._annotations[annotationId];
      for (const [key, value] of Object.entries(this._annotationsByCoursewareId)) {
        if ((value.includes(annotationId) && value.length === 1) || value.length === 0) {
          delete this._annotationsByCoursewareId[key];
        } else if (value.includes(annotationId) && value.length > 1) {
          this._annotationsByCoursewareId[key] = value.filter((annotation) => annotation !== annotationId);
        }
      }
      this._notifySubscribers(notifyingCoursewareIds);
    }
  }

  _addAnnotation = (annotation) => {
    if (typeof annotation.body === 'string' || typeof annotation.target === 'string') {
      throw new Error(`Invalid annotation format for ${annotation.id}. Expecting body and target to be parsed`);
    }
    this._annotations[annotation.id] = annotation;
    this._addAnnotationIdByTarget(annotation.target[0].id, annotation.id);
    annotation.target[0].ancestry?.forEach(({ elementId }) => {
      this._addAnnotationIdByTarget(elementId, annotation.id);
    });
  }

  _addAnnotationIdByTarget(targetId, annotationId) {
    this._annotationsByCoursewareId[targetId] = this._annotationsByCoursewareId[targetId] || [];
    if (!this._annotationsByCoursewareId[targetId].includes(annotationId)) {
      this._annotationsByCoursewareId[targetId].push(annotationId);
    }
  }

  isInitialized() {
    return this.initialized;
  }

  isInitializing() {
    return this.initializing;
  }

  setInitializing(initializing) {
    this.initializing = initializing;
  }

  addCourwareID(id){
    this.coursewareIDs.push(id);
  }
}
